import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { resolveColorHex } from '@/utils/colors';
import { trackModelInteract } from '@/services/analytics';
import { ViewerToolbar } from './ViewerToolbar';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

const DEFAULT_COLOR = '#0ea5e9';
const PLATE_MM = 256;
const PLATE_DIVISIONS = 16;
const AUTO_ROTATE_SPEED = 1.5;
const IDLE_RESUME_MS = 3000;

/* ---- mutable viewer state (lives outside React render cycle) ---- */

interface ViewerState {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  render: () => void;
  // auto-rotate
  animId: number | null;
  idleTimer: ReturnType<typeof setTimeout> | null;
  autoRotateEnabled: boolean;
  // camera presets
  initialCameraPos: THREE.Vector3;
  initialTarget: THREE.Vector3;
  modelCenter: THREE.Vector3;
  cameraDistance: number;
  // helpers added to scene (for cleanup)
  helpers: THREE.Object3D[];
}

/* ---- component ---- */

const PREVIEWABLE_EXTENSIONS = ['3mf', 'stl', 'obj', 'amf'] as const;

/** Z-up formats need -90 deg X rotation to convert to Three.js Y-up */
const Z_UP_FORMATS = new Set(['3mf', 'stl', 'amf']);

const getExtension = (name: string): string =>
  name.split('.').pop()?.toLowerCase() ?? '';

export const isPreviewableFile = (fileName: string): boolean =>
  (PREVIEWABLE_EXTENSIONS as readonly string[]).includes(getExtension(fileName));

interface ModelViewerProps {
  readonly file?: File | null;
  readonly url?: string | null;
  readonly fileName?: string | null;
  readonly color?: string | null;
  readonly className?: string;
}

export const ModelViewer = ({ file, url, fileName, color, className = '' }: ModelViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<ViewerState | null>(null);
  const objectRef = useRef<THREE.Object3D | null>(null);
  const colorRef = useRef(color);

  colorRef.current = color;

  const [isLoading, setIsLoading] = useState(true);
  const [isWireframe, setIsWireframe] = useState(false);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dims, setDims] = useState<{ x: number; y: number; z: number } | null>(null);

  /* ---- cleanup ---- */

  const cleanup = useCallback(() => {
    const s = stateRef.current;
    if (s) {
      if (s.animId !== null) cancelAnimationFrame(s.animId);
      if (s.idleTimer) clearTimeout(s.idleTimer);
      s.helpers.forEach((h) => { s.scene.remove(h); disposeObject(h); });
    }
    if (objectRef.current) {
      disposeObject(objectRef.current);
      stateRef.current?.scene.remove(objectRef.current);
      objectRef.current = null;
    }
    if (s) {
      s.controls.dispose();
      s.renderer.dispose();
      s.renderer.domElement.remove();
    }
    stateRef.current = null;
  }, []);

  /* ---- toolbar callbacks ---- */

  const moveCameraTo = useCallback((pos: THREE.Vector3, target: THREE.Vector3) => {
    const s = stateRef.current;
    if (!s) return;
    s.camera.position.copy(pos);
    s.camera.lookAt(target);
    s.controls.target.copy(target);
    s.controls.update();
    s.render();
  }, []);

  const handleViewFront = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    const c = s.modelCenter;
    moveCameraTo(
      new THREE.Vector3(c.x, c.y, c.z + s.cameraDistance),
      c,
    );
  }, [moveCameraTo]);

  const handleViewTop = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    const c = s.modelCenter;
    moveCameraTo(
      new THREE.Vector3(c.x, c.y + s.cameraDistance, c.z + 0.01),
      c,
    );
  }, [moveCameraTo]);

  const handleViewRight = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    const c = s.modelCenter;
    moveCameraTo(
      new THREE.Vector3(c.x + s.cameraDistance, c.y, c.z),
      c,
    );
  }, [moveCameraTo]);

  const handleViewReset = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    moveCameraTo(s.initialCameraPos.clone(), s.initialTarget.clone());
  }, [moveCameraTo]);

  const handleToggleWireframe = useCallback(() => {
    const obj = objectRef.current;
    if (!obj) return;
    trackModelInteract('wireframe');
    setIsWireframe((prev) => {
      const next = !prev;
      obj.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
          child.material.wireframe = next;
        }
      });
      stateRef.current?.render();
      return next;
    });
  }, []);

  const handleToggleAutoRotate = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    setIsAutoRotating((prev) => {
      const next = !prev;
      s.autoRotateEnabled = next;
      if (next) {
        startLoop(s);
      } else {
        stopLoop(s);
        if (s.idleTimer) { clearTimeout(s.idleTimer); s.idleTimer = null; }
      }
      return next;
    });
  }, []);

  const handleScreenshot = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    trackModelInteract('screenshot');
    s.render();
    const dataUrl = s.renderer.domElement.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = 'model-preview.png';
    link.href = dataUrl;
    link.click();
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    trackModelInteract('fullscreen');
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen();
    }
  }, []);

  /* ---- main scene effect ---- */

  useEffect(() => {
    if ((!file && !url) || !containerRef.current) return;

    cleanup();
    setIsLoading(true);
    setDims(null);
    setIsWireframe(false);
    setIsAutoRotating(true);

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight || 400;

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true, // needed for screenshot
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    // Canvas fills container via CSS; never let setSize set inline styles
    // so the canvas can't inflate the container on fullscreen exit.
    const canvas = renderer.domElement;
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    renderer.setSize(width, height, false);
    container.appendChild(canvas);

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf1f5f9);

    // PBR environment (procedural studio lighting, no network request)
    import('three/addons/environments/RoomEnvironment.js').then(({ RoomEnvironment }) => {
      const pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromScene(new RoomEnvironment()).texture;
      pmrem.dispose();
      renderScene();
    });

    // Supplemental lights (fill + rim)
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
    fillLight.position.set(-2, 3, 2);
    scene.add(fillLight);
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.2);
    rimLight.position.set(2, -1, -3);
    scene.add(rimLight);

    // Camera
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 10000);

    // Controls (damping disabled — avoids needing a constant rAF loop)
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.autoRotateSpeed = AUTO_ROTATE_SPEED;

    // Render on demand
    const renderScene = () => {
      renderer.render(scene, camera);
    };
    controls.addEventListener('change', renderScene);

    // State object
    const vs: ViewerState = {
      renderer,
      scene,
      camera,
      controls,
      render: renderScene,
      animId: null,
      idleTimer: null,
      autoRotateEnabled: true,
      initialCameraPos: new THREE.Vector3(),
      initialTarget: new THREE.Vector3(),
      modelCenter: new THREE.Vector3(),
      cameraDistance: 100,
      helpers: [],
    };
    stateRef.current = vs;

    const addObject = (object: THREE.Object3D, zUp: boolean) => {
      // Use colorRef so we pick up the latest selection even if the
      // model finishes loading after the user already chose a color.
      const meshColorHex = colorRef.current
        ? resolveColorHex(colorRef.current)
        : DEFAULT_COLOR;

      // Apply PBR material
      object.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.material = new THREE.MeshStandardMaterial({
            color: new THREE.Color(meshColorHex),
            roughness: 0.65,
            metalness: 0.0,
          });
        }
      });

      // Z-up formats (3MF, STL, AMF) need rotation to Y-up for Three.js.
      if (zUp) {
        object.rotation.set(-Math.PI / 2, 0, 0);
      }
      object.updateMatrixWorld(true);

      objectRef.current = object;
      scene.add(object);

      // Bounding box (after Z-up → Y-up rotation)
      const box = new THREE.Box3().setFromObject(object);
      const size = box.getSize(new THREE.Vector3());
      const rawCenter = box.getCenter(new THREE.Vector3());

      // Re-center the model so it sits at the grid origin.
      // Shift X/Z to origin, keep Y so the bottom rests on the floor.
      object.position.set(
        object.position.x - rawCenter.x,
        object.position.y - box.min.y,
        object.position.z - rawCenter.z,
      );

      // Recompute bounding box after centering
      const centeredBox = new THREE.Box3().setFromObject(object);
      const center = centeredBox.getCenter(new THREE.Vector3());

      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * (Math.PI / 180);
      const cameraDistance = maxDim / (2 * Math.tan(fov / 2)) * 1.3;

      // Store for presets
      vs.modelCenter = center.clone();
      vs.cameraDistance = cameraDistance;

      // Set initial camera (isometric)
      camera.position.set(
        center.x + cameraDistance * 0.7,
        center.y + cameraDistance * 0.5,
        center.z + cameraDistance * 0.7,
      );
      camera.lookAt(center);
      controls.target.copy(center);
      controls.update();

      vs.initialCameraPos = camera.position.clone();
      vs.initialTarget = center.clone();

      // Dimensions in mm (rotation already applied, axes are correct)
      setDims({
        x: Math.round(size.x * 10) / 10,
        y: Math.round(size.y * 10) / 10,
        z: Math.round(size.z * 10) / 10,
      });

      // Build plate grid (centered at origin, flush with model bottom)
      const grid = new THREE.GridHelper(PLATE_MM, PLATE_DIVISIONS, 0xc0c0c0, 0xdedede);
      grid.position.y = -0.5;
      scene.add(grid);
      vs.helpers.push(grid);

      // Contact shadow
      const shadow = createContactShadow(size, center, -0.3);
      scene.add(shadow);
      vs.helpers.push(shadow);

      // Start auto-rotate
      startLoop(vs);

      // Pause rotation on user interaction, resume after idle
      controls.addEventListener('start', () => {
        if (vs.idleTimer) { clearTimeout(vs.idleTimer); vs.idleTimer = null; }
        stopLoop(vs);
      });
      controls.addEventListener('end', () => {
        if (!vs.autoRotateEnabled) return;
        vs.idleTimer = setTimeout(() => startLoop(vs), IDLE_RESUME_MS);
      });

      setIsLoading(false);
      renderScene();
    };

    // Parse model data based on file extension
    const parseModel = async (data: ArrayBuffer, ext: string) => {
      const zUp = Z_UP_FORMATS.has(ext);

      switch (ext) {
        case '3mf': {
          const { ThreeMFLoader } = await import('three/addons/loaders/3MFLoader.js');
          addObject(new ThreeMFLoader().parse(data), zUp);
          break;
        }
        case 'stl': {
          const { STLLoader } = await import('three/addons/loaders/STLLoader.js');
          const geometry = new STLLoader().parse(data);
          geometry.computeVertexNormals();
          addObject(new THREE.Mesh(geometry), zUp);
          break;
        }
        case 'obj': {
          const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');
          const text = new TextDecoder().decode(data);
          addObject(new OBJLoader().parse(text), zUp);
          break;
        }
        case 'amf': {
          const { AMFLoader } = await import('three/addons/loaders/AMFLoader.js');
          addObject(new AMFLoader().parse(data), zUp);
          break;
        }
        default:
          setIsLoading(false);
      }
    };

    // Determine file extension
    const resolvedName = file?.name ?? fileName ?? '';
    const ext = getExtension(resolvedName);

    if (file) {
      const reader = new FileReader();
      reader.onload = () => { parseModel(reader.result as ArrayBuffer, ext); };
      reader.readAsArrayBuffer(file);
    } else if (url) {
      fetch(url)
        .then((res) => res.arrayBuffer())
        .then((data) => parseModel(data, ext))
        .catch(() => setIsLoading(false));
    }

    // Resize — use ResizeObserver so we catch fullscreen exit reflow.
    // The third arg `false` prevents setSize from setting CSS inline
    // styles, so the canvas never inflates the container.
    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight || 400;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
      renderScene();
    };

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    resizeObserver.observe(container);

    // Fullscreen state tracking
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);

    return () => {
      resizeObserver.disconnect();
      document.removeEventListener('fullscreenchange', handleFsChange);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, url, cleanup]);

  /* ---- color change (no reload) ---- */

  useEffect(() => {
    const obj = objectRef.current;
    if (!obj) return;
    const hex = color ? resolveColorHex(color) : DEFAULT_COLOR;
    obj.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mat = child.material;
        // All meshes get MeshStandardMaterial in addObject, but check
        // for the color property defensively.
        if (mat && 'color' in mat && mat.color instanceof THREE.Color) {
          mat.color.set(hex);
        }
      }
    });
    stateRef.current?.render();
  }, [color]);

  /* ---- render ---- */

  if (!file && !url) return null;

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden rounded-xl bg-slate-100 ${className}`}
      style={{ minHeight: 400, position: 'relative' }}
    >
      {/* Loading overlay */}
      {isLoading && (
        <div
          className="flex flex-col items-center justify-center"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(241, 245, 249, 0.85)',
            zIndex: 10,
            gap: 'var(--space-2)',
          }}
        >
          <LoadingSpinner size="lg" />
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
            Loading model...
          </span>
        </div>
      )}

      {/* Dimensions badge */}
      {dims && !isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 'var(--space-3)',
            left: 'var(--space-3)',
            padding: '3px 8px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 500,
            fontVariantNumeric: 'tabular-nums',
            zIndex: 5,
          }}
        >
          {dims.x} x {dims.y} x {dims.z} mm
        </div>
      )}

      {/* Toolbar */}
      {!isLoading && (
        <ViewerToolbar
          onViewFront={handleViewFront}
          onViewTop={handleViewTop}
          onViewRight={handleViewRight}
          onViewReset={handleViewReset}
          onToggleWireframe={handleToggleWireframe}
          onToggleAutoRotate={handleToggleAutoRotate}
          onScreenshot={handleScreenshot}
          onToggleFullscreen={handleToggleFullscreen}
          isWireframe={isWireframe}
          isAutoRotating={isAutoRotating}
          isFullscreen={isFullscreen}
        />
      )}
    </div>
  );
};

/* ---- auto-rotate loop (only runs rAF while spinning) ---- */

const startLoop = (vs: ViewerState) => {
  if (vs.animId !== null) return;
  vs.controls.autoRotate = true;
  const tick = () => {
    // controls.update() advances auto-rotation and fires 'change',
    // which calls renderScene. No separate render call needed.
    vs.controls.update();
    vs.animId = requestAnimationFrame(tick);
  };
  vs.animId = requestAnimationFrame(tick);
};

const stopLoop = (vs: ViewerState) => {
  vs.controls.autoRotate = false;
  if (vs.animId !== null) {
    cancelAnimationFrame(vs.animId);
    vs.animId = null;
  }
};

/* ---- contact shadow (canvas gradient, no shadow pass) ---- */

const createContactShadow = (
  size: THREE.Vector3,
  center: THREE.Vector3,
  floorY: number,
): THREE.Mesh => {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0.18)');
  gradient.addColorStop(0.6, 'rgba(0, 0, 0, 0.06)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 128, 128);

  const geo = new THREE.PlaneGeometry(size.x * 1.6, size.z * 1.6);
  geo.rotateX(-Math.PI / 2);
  const mat = new THREE.MeshBasicMaterial({
    map: new THREE.CanvasTexture(canvas),
    transparent: true,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(center.x, floorY - 0.3, center.z);
  return mesh;
};

/* ---- dispose helper ---- */

const disposeObject = (obj: THREE.Object3D) => {
  obj.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry.dispose();
      const material = child.material;
      if (Array.isArray(material)) {
        material.forEach((m) => m.dispose());
      } else {
        material.dispose();
      }
    }
  });
};
